/**
 * menu.ts — Categoria "Menu do topo" (10 modelos premium)
 *
 * Barras de navegação e faixas de aviso, combinando `page.nav`, `page.banner`
 * e seções em camadas para variações claras, escuras e com destaque.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { WEB, b } from './_shared';

const LINKS = 'Benefícios|#beneficios\nPlanos|#planos\nPerguntas|#perguntas\nContato|#formulario';
const LINKS_CURTOS = 'Soluções|#solucoes\nContato|#formulario';

/** Atalho para a barra de navegação. */
function nav(props: Record<string, unknown> = {}) {
  return b('page.nav', {
    brand: 'Sua Empresa',
    links: LINKS,
    ctaLabel: 'Fale conosco',
    ctaHref: '#formulario',
    ...props,
  });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'menu.claro',
    category: 'menu',
    label: 'Menu claro',
    description: 'Barra branca com links e botão de contato.',
    preview: 'nav',
    outputs: WEB,
    order: 1,
    build: () => [nav({ backgroundColor: '#ffffff' })],
  },
  {
    key: 'menu.escuro',
    category: 'menu',
    label: 'Menu escuro',
    description: 'Barra escura, boa para páginas com capa clara.',
    preview: 'nav',
    outputs: WEB,
    order: 2,
    build: () => [nav({ backgroundColor: '#0f172a' })],
  },
  {
    key: 'menu.suave',
    category: 'menu',
    label: 'Menu em cinza claro',
    description: 'Fundo cinza discreto, sem contraste forte.',
    preview: 'nav',
    outputs: WEB,
    order: 3,
    build: () => [nav({ backgroundColor: '#f8fafc' })],
  },
  {
    key: 'menu.enxuto',
    category: 'menu',
    label: 'Menu enxuto',
    description: 'Poucos links, ideal para página de campanha.',
    preview: 'nav',
    outputs: WEB,
    order: 4,
    build: () => [nav({ links: LINKS_CURTOS, backgroundColor: '#ffffff' })],
  },
  {
    key: 'menu.com-aviso',
    category: 'menu',
    label: 'Aviso acima do menu',
    description: 'Faixa de destaque seguida da barra de navegação.',
    preview: 'nav',
    outputs: WEB,
    order: 5,
    build: () => [
      b('page.banner', { text: 'Condição especial válida até o fim do mês', linkLabel: 'Ver condições', linkHref: '#planos' }),
      nav({ backgroundColor: '#ffffff' }),
    ],
  },
  {
    key: 'menu.aviso-claro',
    category: 'menu',
    label: 'Aviso claro acima do menu',
    description: 'Faixa em tom suave com menu escuro abaixo.',
    preview: 'nav',
    outputs: WEB,
    order: 6,
    build: () => [
      b('page.banner', { text: 'Atendimento de segunda a sexta, das 8h às 18h', backgroundColor: '#e0f2fe', textColor: '#0c4a6e' }),
      nav({ backgroundColor: '#0f172a' }),
    ],
  },
  {
    key: 'menu.sem-botao',
    category: 'menu',
    label: 'Menu sem botão',
    description: 'Somente marca e links, para páginas institucionais.',
    preview: 'nav',
    outputs: WEB,
    order: 7,
    build: () => [nav({ ctaLabel: '', ctaHref: '', backgroundColor: '#ffffff' })],
  },
  {
    key: 'menu.com-selo',
    category: 'menu',
    label: 'Menu com selo de confiança',
    description: 'Barra de navegação com um selo logo abaixo.',
    preview: 'nav',
    outputs: WEB,
    order: 8,
    build: () => [
      nav({ backgroundColor: '#ffffff' }),
      b('section.layered', { paddingY: 'sm', align: 'center', maxWidth: '1120px' }, [
        b('ui.badge', { text: 'Mais de 2.000 empresas atendidas', variant: 'soft', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'menu.faixa-oferta',
    category: 'menu',
    label: 'Faixa de oferta',
    description: 'Somente a faixa de destaque, sem navegação.',
    preview: 'banner',
    outputs: WEB,
    order: 9,
    build: () => [
      b('page.banner', {
        text: 'Implantação sem custo para contratos fechados neste mês',
        linkLabel: 'Quero aproveitar',
        linkHref: '#formulario',
        backgroundColor: '#111827',
        textColor: '#ffffff',
      }),
    ],
  },
  {
    key: 'menu.completo',
    category: 'menu',
    label: 'Topo completo',
    description: 'Aviso, menu e chamada curta de boas-vindas.',
    preview: 'nav',
    outputs: WEB,
    order: 10,
    build: () => [
      b('page.banner', { text: 'Fale com um especialista hoje mesmo', linkLabel: 'Falar agora', linkHref: '#formulario' }),
      nav({ backgroundColor: '#ffffff' }),
      b('section.layered', { paddingY: 'sm', align: 'center', maxWidth: '900px' }, [
        b('ui.paragraph', { text: 'Soluções sob medida para a sua operação.', align: 'center', size: 'sm' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
